import React, { useEffect, useRef } from "react";

/*
 * Small rich text editor (no dependency): bold, italic, bullet list, numbered list, dash list,
 * undo / redo. It produces plain HTML (p, br, b, i, ul, ol, li); the backend sanitizes it again.
 * Pasted text is inserted as plain text so no foreign formatting comes in.
 */
const STYLE = `
.rte-content { min-height: 110px; padding: 8px 12px; border: 1px solid #ced4da; border-radius: 0 0 4px 4px; outline: none; background: #fff; }
.rte-content:focus { border-color: #80bdff; box-shadow: 0 0 0 0.15rem rgba(0,123,255,.2); }
.rte-content p { margin: 0 0 6px; }
.rte-content ul, .rte-content ol { margin: 4px 0 6px 18px; padding: 0; }
.rte-content ul.dash { list-style: none; margin-left: 6px; }
.rte-content ul.dash li::before { content: "\\2013  "; }
.rte-content:empty::before { content: attr(data-placeholder); color: #6c757d; }
.rte-toolbar { display: flex; flex-wrap: wrap; gap: 4px; padding: 4px 6px; border: 1px solid #ced4da; border-bottom: none; border-radius: 4px 4px 0 0; background: #f8f9fa; }
.rte-toolbar button { border: 0; background: transparent; padding: 2px 9px; border-radius: 4px; font-size: 14px; }
.rte-toolbar button:hover { background: #e9ecef; }
`;

const BUTTONS = [
  { key: "bold", label: <b>G</b>, title: "Gras" },
  { key: "italic", label: <i>I</i>, title: "Italique" },
  { key: "ul", label: "• Liste", title: "Liste à puces" },
  { key: "ol", label: "1. Liste", title: "Liste numérotée" },
  { key: "dash", label: "– Tirets", title: "Liste à tirets" },
  { key: "undo", label: "↶", title: "Annuler" },
  { key: "redo", label: "↷", title: "Rétablir" },
];

const RichTextEditor = ({ id, value, onChange, placeholder, dir }) => {
  const ref = useRef(null);

  // the content is only written back when it differs, so the caret never jumps while typing
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const emit = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const run = (key) => {
    if (!ref.current) return;
    ref.current.focus();
    if (key === "bold") document.execCommand("bold");
    else if (key === "italic") document.execCommand("italic");
    else if (key === "ul") document.execCommand("insertUnorderedList");
    else if (key === "ol") document.execCommand("insertOrderedList");
    else if (key === "undo") document.execCommand("undo");
    else if (key === "redo") document.execCommand("redo");
    else if (key === "dash") {
      // a dash list is a bullet list marked with the "dash" class
      const selection = window.getSelection();
      const node = selection && selection.anchorNode;
      const element = node && (node.nodeType === 1 ? node : node.parentElement);
      let list = element && element.closest ? element.closest("ul") : null;
      if (!list) {
        document.execCommand("insertUnorderedList");
        const after = window.getSelection().anchorNode;
        const el = after && (after.nodeType === 1 ? after : after.parentElement);
        list = el && el.closest ? el.closest("ul") : null;
      }
      if (list && ref.current.contains(list)) list.classList.toggle("dash");
    }
    emit();
  };

  return (
    <div>
      <style>{STYLE}</style>
      <div className="rte-toolbar">
        {BUTTONS.map((b) => (
          <button
            key={b.key}
            type="button"
            title={b.title}
            aria-label={b.title}
            onMouseDown={(e) => {
              e.preventDefault();
              run(b.key);
            }}
          >
            {b.label}
          </button>
        ))}
      </div>
      <div
        id={id}
        ref={ref}
        className="rte-content"
        contentEditable
        suppressContentEditableWarning
        dir={dir}
        data-placeholder={placeholder || ""}
        onFocus={() => document.execCommand("defaultParagraphSeparator", false, "p")}
        onInput={emit}
        onBlur={emit}
        onPaste={(e) => {
          e.preventDefault();
          const text = e.clipboardData.getData("text/plain");
          document.execCommand("insertText", false, text);
        }}
      />
    </div>
  );
};

export default RichTextEditor;
